/* eslint no-param-reassign: 0 */
import { cloneDeep } from 'lodash';
import Vue from 'vue';

/**
 * The BindStatusMixin provides actions, getters, mutations and the initial
 * state used to handle the LDAP bind status of users bound to the LDAP
 * Directory. It is currently used in the SystemUserCollection when used by the
 * SystemUserItemTable in the Directory Details Aside Panel.
 */
export default {
  actions: {
    updateLdapBindingUser({ commit }, payload) {
      const { id, ldapBindingUser } = payload;

      if (ldapBindingUser) {
        commit('ADD_LDAP_BINDING_USER', id);
      } else {
        commit('REMOVE_LDAP_BINDING_USER', id);
      }
    },
  },

  getters: {
    /**
     * Return a hash of user IDs to ldap_binding_user attributes that need to
     * be updated, either because the user's bind status was changed from none
     * to LDAP binding user or from LDAP binding user to none.
     * @returns
     * {
     *   "000": {
     *     "ldap_binding_user": true,
     *   },
     *   "001": {
     *     "ldap_binding_user": false,
     *   },
     * }
     */
    getLdapBindingUsersToUpdate(state) {
      const { ldapBindingUsers, previousLdapBindingUsers } = state.features;
      const ldapBindingUsersToUpdate = {};

      ldapBindingUsers.ids.forEach((id) => {
        if (previousLdapBindingUsers.ids.indexOf(id) === -1) {
          ldapBindingUsersToUpdate[id] = { ldap_binding_user: true };
        }
      });

      previousLdapBindingUsers.ids.forEach((id) => {
        if (ldapBindingUsers.ids.indexOf(id) === -1) {
          ldapBindingUsersToUpdate[id] = { ldap_binding_user: false };
        }
      });

      return ldapBindingUsersToUpdate;
    },

    isLdapBindingUser(state) {
      return id => state.features.ldapBindingUsers.ids.indexOf(id) !== -1;
    },
  },

  mutations: {
    REMOVE_LDAP_BINDING_USER(state, id) {
      const { ids } = state.features.ldapBindingUsers;
      const index = ids.indexOf(id);

      if (index !== -1) {
        ids.splice(index, 1);
      }
    },

    ADD_LDAP_BINDING_USER(state, id) {
      const { ids } = state.features.ldapBindingUsers;

      if (ids.indexOf(id) === -1) {
        ids.push(id);
      }
    },

    ADD_PREVIOUS_LDAP_BINDING_USER(state, id) {
      const { ids } = state.features.previousLdapBindingUsers;

      if (ids.indexOf(id) === -1) {
        ids.push(id);
      }
    },
  },

  addBindStatusFeaturesToState(state) {
    const state2 = cloneDeep(state);

    state2.features.ldapBindingUsers = { ids: [] };
    state2.features.previousLdapBindingUsers = { ids: [] };

    return state2;
  },

  processBindStatusItems({ commit, state }, ids) {
    const { items } = state.entities;

    ids.forEach((id) => {
      const item = items.byId[id];

      if (item && item.ldap_binding_user) {
        commit('ADD_LDAP_BINDING_USER', id);
        commit('ADD_PREVIOUS_LDAP_BINDING_USER', id);
      }
    });
  },

  resetBindStatusState(state) {
    const { ldapBindingUsers, previousLdapBindingUsers } = state.features;
    Vue.set(ldapBindingUsers, 'ids', []);
    Vue.set(previousLdapBindingUsers, 'ids', []);
  },
};
